import React from "react";
import PropTypes from "prop-types";
import { Box, TextField } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
    KeyboardArrowDownRounded,
    KeyboardArrowUpRounded,
} from "@mui/icons-material";
import { colors } from "../../theme/colors";
import {
    COUNTRY_SELECT_FIELD_SX,
    COUNTRY_SELECT_LISTBOX_SX,
    COUNTRY_SELECT_PAPER_SX,
} from "./countrySelectStyles";

export const COUNTRY_SELECT_POPUP_ICON = (
    <>
        <KeyboardArrowDownRounded className="country-select-arrow-down" />
        <KeyboardArrowUpRounded className="country-select-arrow-up" />
    </>
);

function CountrySelect({
    countries,
    loading = false,
    value,
    onChange,
    onInputChange,
    placeholder = "Type to search (e.g. ge → Germany)",
    id,
    inputAriaLabel = "Country",
    sx,
}) {
    return (
        <Autocomplete
            disableClearable
            size="small"
            id={id}
            sx={sx ? [COUNTRY_SELECT_FIELD_SX, sx] : COUNTRY_SELECT_FIELD_SX}
            options={countries}
            value={value}
            loading={loading}
            onChange={onChange}
            popupIcon={COUNTRY_SELECT_POPUP_ICON}
            slotProps={{
                paper: {
                    sx: COUNTRY_SELECT_PAPER_SX,
                },
                listbox: {
                    onWheel: (event) => {
                        event.stopPropagation();
                    },
                    sx: COUNTRY_SELECT_LISTBOX_SX,
                },
            }}
            filterOptions={createFilterOptions({
                stringify: (option) =>
                    `${option.displayName} ${option.code2} ${option.code3}`,
                trim: true,
            })}
            getOptionLabel={(option) => option?.displayName || ""}
            isOptionEqualToValue={(a, b) =>
                !!a && !!b && String(a.code2 || "") === String(b.code2 || "")
            }
            renderOption={(props, option, { selected }) => {
                const { key, ...optionProps } = props;
                return (
                    <Box component="li" key={key} {...optionProps}>
                        <Box
                            sx={{
                                display: "flex",
                                flex: 1,
                                minWidth: 0,
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "0.75rem",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    minWidth: 0,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {option.displayName}
                            </Box>
                            {selected ? (
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        flexShrink: 0,
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        color: colors.accent.soft,
                                    }}
                                >
                                    ✓
                                </Box>
                            ) : null}
                        </Box>
                    </Box>
                );
            }}
            renderInput={({ InputProps, inputProps, ...restParams }) => (
                <TextField
                    {...restParams}
                    placeholder={placeholder}
                    slotProps={{
                        htmlInput: {
                            ...inputProps,
                            "aria-label": inputAriaLabel,
                            onChange: (event) => {
                                inputProps?.onChange?.(event);
                                onInputChange?.(event.target.value);
                            },
                        },
                        input: {
                            ...InputProps,
                        },
                    }}
                />
            )}
        />
    );
}

CountrySelect.propTypes = {
    countries: PropTypes.arrayOf(PropTypes.object).isRequired,
    loading: PropTypes.bool,
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onInputChange: PropTypes.func,
    placeholder: PropTypes.string,
    id: PropTypes.string,
    inputAriaLabel: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default React.memo(CountrySelect);
